import { ActionBuilderType } from "@builder/utils/actionBuilderHelpers";
import { BuilderAction } from "@common/build/types";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { v4 } from "uuid";

interface InitialState {
    current: Current;
    activeStepperIndex: number | null;
    actionsEditor: ActionEditorItem[];
}

export type ActionEditorItem = {
    id?: string;
    type: ActionBuilderType;
    title: string;
    action?: BuilderAction;
    isAuto?: boolean;
    name?: string;
    noTranslate?: boolean;
};

interface Current {
    tab?: ActionBuilderType | null;
    id: string | null;
}

const initialState: InitialState = {
    current: { tab: ActionBuilderType.START, id: null as string | null },
    activeStepperIndex: 0 as number | null,
    actionsEditor: [
        {
            id: v4(),
            type: ActionBuilderType.START,
            title: "Action start",
            action: null,
        }
    ]
};

const actionBuilderSlice = createSlice({
    name: "actionBuilder",
    initialState: initialState,
    reducers: {
        setCurrent: (state, action: PayloadAction<Current>) => {
            state.current = action.payload;
        },
        setActiveStepperIndex: (state, action: PayloadAction<number>) => {
            state.activeStepperIndex = action.payload;
        },
        updateActionEditor: (state, action: PayloadAction<{ index?: number; item: ActionEditorItem }>) => {
            if (action.payload.index !== undefined) {
                state.actionsEditor.splice(action.payload.index, 0, action.payload.item);
                state.activeStepperIndex = action.payload.index;
            } else {
                state.actionsEditor.push(action.payload.item);
                state.activeStepperIndex = state.actionsEditor.length - 1;
            }
        },
        setActionsEditor: (state, action: PayloadAction<ActionEditorItem[]>) => {
            state.actionsEditor = action.payload;
            state.activeStepperIndex = 0;
        },
        removeBuilderAction: (state, action: PayloadAction<string | null>) => {
            if (!action.payload) return;

            state.actionsEditor = state.actionsEditor.filter(actionItem => actionItem.id !== action.payload);

            const lastIndex = state.actionsEditor.length - 1;
            const lastAction = state.actionsEditor[lastIndex];

            if (lastAction) {
                state.activeStepperIndex = lastIndex;
                state.current.tab = lastAction.type;
                state.current.id = lastAction.id || "";
            } else {
                state.activeStepperIndex = null;
                state.current.tab = null;
                state.current.id = null;
            }
        },
        updateStepperActions: (state, action: PayloadAction<{ id: string | null; item: ActionEditorItem }>) => {
            if (!action.payload.id) return;

            const actionToUpdate = state.actionsEditor.find(actionItem => actionItem.id === action.payload.id);
            if (actionToUpdate) {
                Object.assign(actionToUpdate, action.payload.item);
            }
        },
        clearActionsEditor: state => {
            state.actionsEditor = [
                {
                    id: v4(),
                    type: ActionBuilderType.START,
                    title: "Action start",
                    action: null
                }
            ];
            state.activeStepperIndex = 0;
            state.current = { tab: ActionBuilderType.START, id: null };
        }
    }
});

export default actionBuilderSlice.reducer;
export const {
    setCurrent,
    setActiveStepperIndex,
    updateActionEditor,
    setActionsEditor,
    removeBuilderAction,
    updateStepperActions,
    clearActionsEditor
} = actionBuilderSlice.actions;
